import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "../supabaseClient";

const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider"
    );
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrganizations = async (selectOrganizationId) => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrganization(null);
      setLoading(false);
      return;
    }

    try {
      // First get the organization members with their roles
      const { data: memberData, error: memberError } = await supabase
        .from("organizationMembers")
        .select(
          `
          id,
          organizationID,
          status,
          memberRoles:organizationMemberRoles (
            id,
            roleID,
            role:organizationRoles (
              id,
              name,
              description,
              permissions,
              rank
            )
          ),
          organization:organizations (
            id,
            name,
            nameAbbreviation,
            description,
            orgData
          )
        `
        )
        .eq("userID", user.id)
        .eq("status", "active");

      if (memberError) throw memberError;

      const orgs =
        memberData?.map((member) => ({
          ...member.organization,
          memberRoles: member.memberRoles,
        })) || [];

      setOrganizations(orgs);

      if (selectOrganizationId) {
        const selected = orgs.find((org) => org.id === selectOrganizationId);
        if (selected) {
          setCurrentOrganization(selected);
        }
      } else if (!currentOrganization && orgs.length > 0) {
        setCurrentOrganization(orgs[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [user]);

  const switchOrganization = (organizationId) => {
    const org = organizations.find((org) => org.id === organizationId);
    if (org) {
      setCurrentOrganization(org);
    }
  };

  const value = {
    organizations,
    currentOrganization,
    loading,
    error,
    switchOrganization,
    refreshOrganizations: fetchOrganizations,
    userRole: "member", // TODO: Implement role system
    permissions: [], // TODO: Implement permissions system
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
